/**
 * @description Provides a hash function for a given type.
 */

interface Hash<T> {
  hash: (data: T) => string;
}

export const hashString: Hash<string> = {
  hash: (data) => data,
};

export const hashNumber: Hash<number> = {
  hash: (data) => data.toString(),
};

export const hashBoolean: Hash<boolean> = {
  hash: (data) => data.toString(),
};

export const hashDate: Hash<Date> = {
  hash: (data) => data.toISOString(),
};

export const getHashPair = <T, U>([hash1, hash2]: [Hash<T>, Hash<U>]): Hash<
  [T, U]
> => ({
  hash: ([a, b]) => `${hash1.hash(a)}:${hash2.hash(b)}`,
});

export const getHashArray = <T>(hash: Hash<T>): Hash<T[]> => ({
  hash: (data) => data.map(hash.hash).join(','),
});

export const getHashRecord = <K extends string | number | symbol, T>(
  hashKey: Hash<K>,
  hashValue: Hash<T>
): Hash<Record<K, T>> => ({
  hash: (data) => {
    const keys = Object.keys(data).sort();
    return keys
      .map((k) => `${hashKey.hash(k as K)}:${hashValue.hash(data[k as K]!)}`)
      .join(',');
  },
});

export const getHashSet = <T>(hash: Hash<T>): Hash<Set<T>> => ({
  hash: (data) => {
    const values = Array.from(data).sort();
    return values.map(hash.hash).join(',');
  },
});

export const getHashMap = <K, V>(
  hashKey: Hash<K>,
  hashValue: Hash<V>
): Hash<Map<K, V>> => ({
  hash: (data) => {
    const keys = Array.from(data.keys()).sort();
    return keys
      .map((k) => `${hashKey.hash(k)}:${hashValue.hash(data.get(k)!)}`)
      .join(',');
  },
});

type GetHashInterfaceArgs<T> = {
  [K in keyof T]: Hash<T[K]>;
};

export const getHashInterface = <T extends object>(
  hashes: GetHashInterfaceArgs<T>
): Hash<T> => ({
  hash: (data) => {
    const keys = Object.keys(data).sort() as (keyof T)[];
    return keys.map((k) => `${hashes[k].hash(data[k])}`).join(',');
  },
});

export const hashAll = <T>(data: T): string => {
  if (typeof data === 'string') return hashString.hash(data);
  if (typeof data === 'number') return hashNumber.hash(data);
  if (typeof data === 'boolean') return hashBoolean.hash(data);
  if (data instanceof Date) return hashDate.hash(data);
  if (Array.isArray(data)) return getHashArray({ hash: hashAll }).hash(data);
  if (data instanceof Set) return getHashSet({ hash: hashAll }).hash(data);
  if (data instanceof Map)
    return getHashMap({ hash: hashAll }, { hash: hashAll }).hash(data);
  if (typeof data === 'object')
    return getHashRecord({ hash: hashAll }, { hash: hashAll }).hash(
      data as Record<string, unknown>
    );
  throw new Error(`Cannot hash value of type ${typeof data}`);
};
